import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Form, Input, Button, DatePicker, Select, InputNumber,
  Table, Space, Card, Collapse, Row, Col, App, Spin, Typography, Alert, Modal, Tooltip, Tag, Steps, Segmented, Descriptions
} from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import api from '../services/api';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { OrderDto, OrderOptionDto, normalizeOrder, normalizeOrderOptions } from '../dto/order';
import { buildRecipientFieldsFromCustomer, mapOrderProductsToWZItems } from '../dto/wz';
import { fetchOrders, generateWzFromOrder } from '../modules/orders/api/ordersApi';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import { formatDate } from '../utils/dateUtils';
import { getApiErrorMessage } from '../utils/apiError';
import { formatConversionValue, WZConversionUnit } from '../utils/dimensionConversion';
import { formatUnit } from '../utils/units';
import { formatProductDimensions } from '../utils/productDimensions';
import { authService } from '../services/auth';
import ProductNameCombobox from '../components/inventory/ProductNameCombobox';
import CustomerNameCombobox from '../components/inventory/CustomerNameCombobox';
import { buildWzVariantAutofill } from '../utils/wzVariantSelection';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import {
  WAREHOUSE_QUANTITY_MIN,
  WAREHOUSE_QUANTITY_PRECISION,
  WZ_QUANTITY_INVALID_CODE,
  WZ_QUANTITY_MAX,
} from '../utils/warehouseQuantity';
import type { TransportRouteSummaryDto } from '../dto/transport';
import { buildWZTransportFormPatch, buildWZTransportOption } from '../utils/wzTransportSelection';
import { WZItemCard, WZItemField, WZItemsList } from './WZItemsView';
import './WZItemsView.css';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface WZItem {
  productVariantId?: string;
  productName: string;
  productCode?: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
  totalValue?: number;
  description?: string;
  conversionUnit?: WZConversionUnit | null;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  weight?: number;
  netWeight?: number;
  grossWeight?: number;
}

const formatWZItemDimensions = (item: WZItem) => formatProductDimensions({
  width: item.width,
  height: item.height,
  length: item.length,
  thickness: item.thickness,
  productCategoryDimensionDisplayProfile: 'GENERIC',
}) || '—';

// Backend WZ validation strings are hardcoded English (no backend i18n). Map the
// known required-field messages to wz:serverErrors.* keys so the toast is localized.
// Unmapped strings fall through to the raw text — no silent loss.
const WZ_SERVER_ERROR_KEYS: Record<string, string> = {
  [WZ_QUANTITY_INVALID_CODE]: 'wz:serverErrors.quantityPrecision',
  'Order ID is required': 'wz:serverErrors.orderRequired',
  'Release date is required': 'wz:serverErrors.releaseDateRequired',
  // Route-layer (express-validator) variant of the same field — different wording.
  'Valid release date is required': 'wz:serverErrors.releaseDateRequired',
  'Issuer name is required': 'wz:serverErrors.issuerNameRequired',
  'Issuer address is required': 'wz:serverErrors.issuerAddressRequired',
  'Recipient name is required': 'wz:serverErrors.recipientNameRequired',
  'Recipient address is required': 'wz:serverErrors.recipientAddressRequired',
  'Issued by person is required': 'wz:serverErrors.issuedByRequired',
  'Issued by person must be at most 100 characters': 'wz:serverErrors.personNameMax',
  'Received by person must be at most 100 characters': 'wz:serverErrors.personNameMax',
  'At least one item is required': 'wz:serverErrors.itemsRequired',
};

// WZ only makes sense once the order is confirmed and past the sales phase.
// Quotes, inquiries, on-hold and cancelled orders should not surface as
// selectable in either the auto-generate picker or the manual order Select.
// Single home so both flows stay in lockstep.
const WZ_ELIGIBLE_STATUSES = new Set([
  'CONFIRMED',
  'IN_FULFILLMENT',
  'READY_FOR_DISPATCH',
  'IN_DELIVERY',
  'PARTIALLY_DELIVERED',
  'DELIVERED',
]);

const isStaleWZOrderError = (error: any) => (
  error?.response?.data?.code === 'WZ_ORDER_NOT_FOUND'
);

const WZNew: React.FC = () => {
  const { t } = useTranslation(['wz', 'orders', 'common', 'transport']);
  // antd v5 static message.* silently no-ops under React 19; use the App context
  // instance so toasts actually render (matches CustomerForm/Settings/Reports).
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const autoGenerate = searchParams.get('autoGenerate') === 'true' && !orderId;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [items, setItems] = useState<WZItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [deliveryAddressMode, setDeliveryAddressMode] = useState<'registered' | 'custom'>('registered');
  const [pickerOpen] = useState(autoGenerate);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerOrders, setPickerOrders] = useState<OrderDto[]>([]);
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [ordersWithWZ, setOrdersWithWZ] = useState<Map<string, { id: string; wzNumber: string }>>(new Map());
  const [orderOptions, setOrderOptions] = useState<OrderOptionDto[]>([]);
  const [orderOptionsLoading, setOrderOptionsLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [generatingFromOrderId, setGeneratingFromOrderId] = useState<string | null>(null);
  const [orderSelectLoading, setOrderSelectLoading] = useState(false);
  // Guards a rapid re-pick in the step-0 order Select: only the full-order
  // fetch for the most recently selected order is allowed to reach the form.
  const orderSelectRequestRef = useRef(0);
  const canEditSalePrices = authService.canEditCustomerSalePrices();
  const canEditPurchasePrices = authService.canEditWarehouseDocumentPurchasePrices();
  const currentUserDisplayName = authService.getCurrentUser()?.name || '';
  const selectedOrderId = Form.useWatch('orderId', form);

  // Shared fill logic for BOTH the ?orderId= URL preload path and the manual
  // order Select onChange — mirrors WZDetail.handleOrderChange plus the item
  // mapping this page already had. Kept as one function so the two entry
  // points can never drift apart.
  const applyOrderToForm = useCallback((orderData: OrderDto | OrderOptionDto) => {
    const customer = orderData.customer;
    const products = Array.isArray(orderData.products) ? orderData.products : [];

    form.setFieldsValue({
      orderId: orderData.id,
      ...buildRecipientFieldsFromCustomer(customer),
      deliveryAddress: orderData.deliveryLocation || customer?.address || '',
      clientOrderNumber: orderData.clientOrderNumber || '',
      currency: orderData.currency || 'PLN',
    });
    setDeliveryAddressMode(
      orderData.deliveryLocation && orderData.deliveryLocation !== customer?.address
        ? 'custom'
        : 'registered'
    );

    // Convert order products to WZ items
    const wzItems: WZItem[] = mapOrderProductsToWZItems({ products }).map(item => ({
      productName: item.productName || '',
      productCode: item.productCode || '',
      quantity: item.quantity || 1,
      unit: item.unit || 'pcs',
      unitPrice: canEditSalePrices ? item.unitPrice : 0,
      totalValue: canEditSalePrices ? item.totalValue : 0,
      description: item.notes || '',
      conversionUnit: item.conversionUnit ?? null,
      width: item.width,
      height: item.height,
      length: item.length,
      thickness: item.thickness
    }));
    setItems(wzItems);
  }, [form, canEditSalePrices]);

  // Load order data if orderId is provided
  useEffect(() => {
    const loadOrderData = async (id: string) => {
      setLoading(true);
      try {
        const response = await api.get(`/orders/${id}`);
        const orderData = normalizeOrder(response.data, 'wz.new.order');
        setOrder(orderData);
        applyOrderToForm(orderData);
      } catch (error) {
        console.error('Error loading order:', error);
        message.error(t('wz:errors.loadOrder'));
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      loadOrderData(orderId);
    } else {
      // Initialize with empty item
      setItems([{
        productName: '',
        productCode: '',
        quantity: 1,
        unit: 'pcs',
        unitPrice: 0,
        totalValue: 0,
        description: ''
      }]);
    }
  }, [orderId, t, message, applyOrderToForm]);

  // Load the eligible order list for the manual "Zamówienie" picker (step 0).
  // Skipped in the autoGenerate flow, which uses its own pickerOrders below.
  useEffect(() => {
    if (autoGenerate) return;
    let cancelled = false;
    const loadOrderOptions = async () => {
      setOrderOptionsLoading(true);
      try {
        const response = await api.get('/orders');
        if (cancelled) return;
        const allOptions = normalizeOrderOptions(response.data, 'wz.new.orderOptions');
        setOrderOptions(allOptions.filter(o => WZ_ELIGIBLE_STATUSES.has(o.status)));
      } catch (error) {
        console.error('Error loading orders for WZ order picker:', error);
      } finally {
        if (!cancelled) setOrderOptionsLoading(false);
      }
    };
    loadOrderOptions();
    return () => { cancelled = true; };
  }, [autoGenerate]);

  const handleOrderSelect = async (id: string) => {
    const selected = orderOptions.find(o => o.id === id);
    if (selected) {
      applyOrderToForm(selected);
    }

    // GET /orders (which feeds orderOptions) returns a perf-trimmed customer
    // projection without `address` (see orderRepository.findAll) — unlike the
    // ?orderId= preload path and "Generuj z zamówienia", which both read the
    // full order via GET /orders/:id (findById, full customer row). Refetch
    // the full order here so recipientAddress fills the same way those flows
    // do; city/postal/country stay blank either way since Customer only has
    // one free-text address field.
    const requestId = ++orderSelectRequestRef.current;
    setOrderSelectLoading(true);
    try {
      const response = await api.get(`/orders/${id}`);
      if (orderSelectRequestRef.current !== requestId) return; // superseded by a newer pick
      applyOrderToForm(normalizeOrder(response.data, 'wz.new.orderSelect'));
    } catch (error) {
      console.error('Error loading full order for WZ recipient autofill:', error);
    } finally {
      if (orderSelectRequestRef.current === requestId) setOrderSelectLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const orderIdForTransports = String(selectedOrderId || '').trim();

    form.setFieldsValue({
      transportIds: [],
      transportCompany: undefined,
      transportPrice: undefined,
      transportInvoiceNo: undefined,
      vehicleRegistration: undefined,
      driverName: undefined,
    });

    if (!orderIdForTransports) {
      setTransports([]);
      return () => { cancelled = true; };
    }

    const loadTransports = async () => {
      try {
        const result = await fetchOrderTransports(orderIdForTransports);
        if (!cancelled) {
          setTransports(result.transports.filter((transport) => transport.id));
        }
      } catch (error) {
        console.error('Error loading order transports:', error);
        if (!cancelled) {
          setTransports([]);
        }
      }
    };
    loadTransports();
    return () => { cancelled = true; };
  }, [selectedOrderId, form]);

  // Load orders + existing WZ when picker is open (autoGenerate flow)
  useEffect(() => {
    if (!pickerOpen) return;
    let cancelled = false;
    const loadPickerData = async () => {
      setPickerLoading(true);
      try {
        const [orders, wzResponse] = await Promise.all([
          fetchOrders({ isQuote: false, sortBy: 'orderDate', sortOrder: 'desc' }, 'wz.new.picker'),
          api.get('/wz'),
        ]);
        if (cancelled) return;
        const selectable = orders.filter(o => WZ_ELIGIBLE_STATUSES.has(o.status));
        const takenByOrder = new Map<string, { id: string; wzNumber: string }>();
        if (Array.isArray(wzResponse.data)) {
          for (const d of wzResponse.data) {
            const oId = typeof d?.orderId === 'string' ? d.orderId : '';
            // Keep the first (newest — list is ordered createdAt desc) WZ per order.
            if (oId && d?.id && !takenByOrder.has(oId)) {
              takenByOrder.set(oId, { id: String(d.id), wzNumber: d.wzNumber || '' });
            }
          }
        }
        setPickerOrders(selectable);
        setOrdersWithWZ(takenByOrder);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading picker data:', error);
          message.error(t('wz:pickOrder.loadFailed'));
        }
      } finally {
        if (!cancelled) setPickerLoading(false);
      }
    };
    loadPickerData();
    return () => { cancelled = true; };
  }, [pickerOpen, t, message]);

  const filteredPickerOrders = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return pickerOrders;
    return pickerOrders.filter(o => (
      (o.displayOrderNumber || o.orderNumber || '').toLowerCase().includes(q)
      || (o.customerName || '').toLowerCase().includes(q)
    ));
  }, [pickerOrders, pickerSearch]);

  const handleViewExistingWZ = (existingWZ: { id: string; wzNumber: string }) => {
    message.info(t('wz:pickOrder.alreadyHasWZToast', { number: existingWZ.wzNumber || t('wz:newDocument') }));
    navigate(`/app/wz/${existingWZ.id}`);
  };

  const handlePickOrder = async (pickedOrder: OrderDto) => {
    const existingWZ = ordersWithWZ.get(pickedOrder.id);
    if (existingWZ) {
      handleViewExistingWZ(existingWZ);
      return;
    }
    setGeneratingFromOrderId(pickedOrder.id);
    try {
      const created = await generateWzFromOrder(pickedOrder.id) as { id?: string };
      message.success(t('orders:orderDetail.messages.wzGeneratedSuccess'));
      if (created?.id) {
        navigate(`/app/wz/${created.id}?mode=edit`);
      } else {
        navigate('/app/wz');
      }
    } catch (error: any) {
      // Surface the specific backend reason (e.g. "Recipient address is required")
      // instead of a generic failure — this is the "explain why" the picker promises.
      message.error(getApiErrorMessage(error, t, 'orders:orderDetail.messages.wzGenerateFailed', WZ_SERVER_ERROR_KEYS));
      if (isStaleWZOrderError(error)) {
        setPickerOrders((current) => current.filter((candidate) => candidate.id !== pickedOrder.id));
      }
      setGeneratingFromOrderId(null);
    }
  };

  const handleCancelPicker = () => {
    navigate('/app/wz');
  };

  const handleAddItem = () => {
    setItems([...items, {
      productName: '',
      productCode: '',
      quantity: 1,
      unit: 'pcs',
      unitPrice: 0,
      totalValue: 0,
      description: ''
    }]);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems.length > 0 ? newItems : [{
      productName: '',
      productCode: '',
      quantity: 1,
      unit: 'pcs',
      unitPrice: 0,
      totalValue: 0,
      description: ''
    }]);
  };

  const handleItemChange = (index: number, field: keyof WZItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Calculate total value if quantity or unit price changes
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : newItems[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : newItems[index].unitPrice;
      newItems[index].totalValue = (quantity || 0) * (unitPrice || 0);
    }

    setItems(newItems);
  };

  /** Free-text edit of the product name combobox detaches any catalog link —
   *  the operator is describing a product not in the catalog (or has edited
   *  away from a previously picked one). A catalog pick right after this
   *  (onSelectVariant below) fires as a separate event and supersedes it with
   *  the full autofill, so the two can't race. */
  const handleProductNameTyped = (index: number, productName: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], productName, productVariantId: undefined };
    setItems(newItems);
  };

  const transportOptions = useMemo(() => (
    transports.map((transport) => buildWZTransportOption(transport, t))
  ), [transports, t]);

  const handleTransportSelect = (transportIds?: string[] | string) => {
    const { patch, snapshot } = buildWZTransportFormPatch(
      transports,
      transportIds,
      canEditPurchasePrices,
      form.getFieldValue('deliveryAddress'),
    );
    form.setFieldsValue({
      ...patch,
    });
    if (snapshot.deliveryAddress) {
      setDeliveryAddressMode('custom');
    }
  };

  const stepItems = [
    { title: t('wz:wizard.document') },
    { title: t('wz:wizard.recipient') },
    { title: t('wz:wizard.delivery') },
    { title: t('wz:wizard.items') },
    { title: t('wz:wizard.summary') },
  ];

  // Read-only order label for the Podsumowanie recap — prefers the fully
  // loaded ?orderId= order, falls back to the manual-picker option list.
  const selectedOrderSummaryLabel = useMemo(() => {
    if (order && order.id === selectedOrderId) {
      return `${order.displayOrderNumber || order.orderNumber}${order.customerName ? ` - ${order.customerName}` : ''}`;
    }
    const found = orderOptions.find(o => o.id === selectedOrderId);
    if (found) {
      return found.label || `${found.displayOrderNumber || found.orderNumber} - ${found.customerName || '-'}`;
    }
    return '';
  }, [order, orderOptions, selectedOrderId]);

  const getCurrentStepFields = () => {
    switch (currentStep) {
      case 0:
        return ['orderId', 'issueDate', 'releaseDate', 'currency', 'issuedBy'];
      case 1:
        return ['recipientName', 'recipientAddress'];
      default:
        return [];
    }
  };

  const hasValidItems = () => items.some(item => item.productName.trim() !== '');

  const handleNextStep = async () => {
    const fields = getCurrentStepFields();
    if (fields.length > 0) {
      try {
        await form.validateFields(fields);
      } catch {
        return;
      }
    }

    if (currentStep === 3 && !hasValidItems()) {
      setValidationError(t('wz:errors.noItems'));
      message.error(t('wz:errors.noItems'));
      return;
    }

    if (currentStep === 1 && deliveryAddressMode === 'registered') {
      form.setFieldsValue({ deliveryAddress: form.getFieldValue('recipientAddress') || '' });
    }

    setValidationError(null);
    setCurrentStep(step => Math.min(step + 1, stepItems.length - 1));
  };

  const handleBackStep = () => {
    setCurrentStep(step => Math.max(step - 1, 0));
  };

  const handleDeliveryModeChange = (mode: string | number) => {
    const nextMode = mode === 'custom' ? 'custom' : 'registered';
    setDeliveryAddressMode(nextMode);
    if (nextMode === 'registered') {
      form.setFieldsValue({ deliveryAddress: form.getFieldValue('recipientAddress') || '' });
    }
  };

  const handleSubmit = async (values: any) => {
    // Defense in depth: the "Utwórz" submit button only renders on the last
    // (Podsumowanie) step, but a stray Enter keypress on an earlier step could
    // otherwise reach this handler via the form's native submit. Refuse to
    // create the document unless the wizard is actually on the review step.
    if (currentStep !== stepItems.length - 1) {
      return;
    }

    if (!values.releaseDate) {
      setValidationError(t('wz:validation.releaseDateRequired'));
      message.error(t('wz:validation.releaseDateRequired'));
      return;
    }

    const validItems = items.filter(item => item.productName.trim() !== '');
    if (validItems.length === 0) {
      setValidationError(t('wz:errors.noItems'));
      message.error(t('wz:errors.noItems'));
      return;
    }

    setValidationError(null);

    setSubmitting(true);
    try {
      const safeItems = canEditSalePrices
        ? validItems
        : validItems.map(({ unitPrice, totalValue, ...item }) => item);
      const wzData: any = {
        ...values,
        issueDate: values.issueDate ? values.issueDate.format('YYYY-MM-DD') : undefined,
        releaseDate: values.releaseDate.toISOString(),
        receivedDate: values.receivedDate?.toISOString(),
        transportIds: Array.isArray(values.transportIds) ? values.transportIds : [],
        items: safeItems,
      };

      if (canEditPurchasePrices) {
        wzData.purchaseInvoiceAmount = values.purchaseInvoiceAmount || 0;
      } else {
        delete wzData.producerInvoice;
        delete wzData.purchaseInvoiceAmount;
        delete wzData.transportPrice;
        delete wzData.transportInvoiceNo;
      }

      const response = await api.post('/wz', wzData);
      message.success(t('wz:messages.createSuccess'));
      const createdId = response?.data?.id;
      navigate(createdId ? `/app/wz/${createdId}` : '/app/wz');
    } catch (error: any) {
      console.error('Error creating WZ document:', error);
      message.error(getApiErrorMessage(error, t, 'wz:errors.createFailed', WZ_SERVER_ERROR_KEYS));
      if (isStaleWZOrderError(error)) {
        const staleOrderId = String(values.orderId || '');
        orderSelectRequestRef.current += 1;
        setOrder(null);
        setOrderOptions((current) => current.filter((candidate) => candidate.id !== staleOrderId));
        form.setFieldsValue({ orderId: undefined, transportIds: [] });
        setItems([{
          productName: '',
          productCode: '',
          quantity: 1,
          unit: 'pcs',
          unitPrice: 0,
          totalValue: 0,
          description: '',
        }]);
        setCurrentStep(0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const pickerColumns = [
    {
      title: t('wz:pickOrder.fields.orderNumber'),
      dataIndex: 'displayOrderNumber',
      key: 'orderNumber',
      render: (_: any, record: OrderDto) => record.displayOrderNumber || record.orderNumber || '—',
    },
    {
      title: t('wz:pickOrder.fields.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      render: (value: string) => value || '—',
    },
    {
      title: t('wz:pickOrder.fields.orderDate'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (value: string) => value ? formatDate(value) : '—',
      width: 130,
    },
    {
      title: t('wz:pickOrder.fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => <Tag>{t(`orders:statusOptions.${value}`, { defaultValue: value.replace(/_/g, ' ') })}</Tag>,
      width: 140,
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_: any, record: OrderDto) => {
        const existingWZ = ordersWithWZ.get(record.id);
        if (existingWZ) {
          return (
            <Tooltip title={t('wz:pickOrder.alreadyHasWZ')}>
              <Button
                size="small"
                onClick={() => handleViewExistingWZ(existingWZ)}
              >
                {t('wz:pickOrder.viewExisting', { number: existingWZ.wzNumber || t('wz:newDocument') })}
              </Button>
            </Tooltip>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            loading={generatingFromOrderId === record.id}
            onClick={() => handlePickOrder(record)}
          >
            {t('wz:pickOrder.select')}
          </Button>
        );
      },
    },
  ];

  const pickerModal = (
    <Modal
      title={t('wz:pickOrder.title')}
      open={pickerOpen}
      onCancel={handleCancelPicker}
      footer={null}
      width={860}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Input.Search
          placeholder={t('wz:pickOrder.searchPlaceholder')}
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
          allowClear
        />
        <Table
          dataSource={filteredPickerOrders}
          columns={pickerColumns}
          rowKey="id"
          loading={pickerLoading}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: t('wz:pickOrder.noOrders') }}
        />
      </Space>
    </Modal>
  );

  if (autoGenerate) {
    return (
      <div style={{ padding: '24px' }}>
        <Spin spinning={pickerLoading || !!generatingFromOrderId}>
          <div style={{ minHeight: 200 }} />
        </Spin>
        {pickerModal}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {pickerModal}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>
            <ArrowLeftOutlined
              onClick={() => navigate('/app/wz')}
              style={{ marginRight: '16px', cursor: 'pointer' }}
            />
            {t('wz:newDocument')}
          </Title>
        </div>

        <Steps
          current={currentStep}
          items={stepItems}
          style={{ marginBottom: 8 }}
          responsive
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={() => message.warning(t('wz:fillRequired'))}
          scrollToFirstError
          initialValues={{
            issueDate: moment(),
            releaseDate: moment(),
            currency: 'PLN',
            status: 'DRAFT',
            transportIds: [],
            issuedBy: currentUserDisplayName,
            receivedBy: currentUserDisplayName,
          }}
        >
          <Card
            title={t('wz:sections.basicInfo')}
            style={{ marginBottom: '24px', display: currentStep === 0 ? 'block' : 'none' }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="orderId"
                  label={t('wz:fields.order')}
                  rules={[{ required: true, message: t('wz:validation.orderRequired') }]}
                >
                  <Select
                    showSearch
                    disabled={!!order}
                    loading={orderOptionsLoading || orderSelectLoading}
                    placeholder={t('wz:placeholders.selectOrder')}
                    onChange={handleOrderSelect}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {orderOptions.map(o => (
                      <Option key={o.id} value={o.id}>
                        {o.label || `${o.displayOrderNumber || o.orderNumber} - ${o.customerName || '-'}`}
                      </Option>
                    ))}
                    {order && !orderOptions.some(o => o.id === order.id) && (
                      <Option key={order.id} value={order.id}>
                        {`${order.displayOrderNumber || order.orderNumber} - ${order.customerName || '-'}`}
                      </Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="issueDate"
                  label={t('wz:fields.issueDate')}
                  rules={[{ required: true, message: t('wz:validation.issueDateRequired') }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="releaseDate"
                  label={t('wz:fields.releaseDate')}
                  rules={[{ required: true, message: t('wz:validation.releaseDateRequired') }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="receivedDate"
                  label={t('wz:fields.receivedDate')}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="currency"
                  label={t('wz:fields.currency')}
                  rules={[{ required: true }]}
                >
                  <Select>
                    <Option value="PLN">PLN</Option>
                    <Option value="EUR">EUR</Option>
                    <Option value="USD">USD</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="clientOrderNumber"
                  label={t('wz:fields.clientOrderNumber')}
                >
                  <Input placeholder={t('wz:placeholders.clientOrderNumber')} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={t('wz:sections.signature')}
            style={{ marginBottom: '24px', display: currentStep === 0 ? 'block' : 'none' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="issuedBy"
                  label={t('wz:fields.issuedBy')}
                  rules={[
                    { required: true, message: t('wz:validation.issuedByRequired') },
                    { max: 100, message: t('wz:validation.personNameMax') },
                  ]}
                >
                  <Input placeholder={t('wz:placeholders.issuedBy')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="receivedBy"
                  label={t('wz:fields.receivedBy')}
                  rules={[{ max: 100, message: t('wz:validation.personNameMax') }]}
                >
                  <Input placeholder={t('wz:placeholders.receivedBy')} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={t('wz:sections.recipient')}
            style={{ marginBottom: '24px', display: currentStep === 1 ? 'block' : 'none' }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                {/* Pick-or-type combobox: value IS recipientName, so a loaded
                    or freely-typed recipient always displays natively.
                    Picking a customer suggestion additionally fills the rest
                    of the recipient block via buildRecipientFieldsFromCustomer;
                    the fields stay editable for a one-off recipient not in
                    the list, and picking an order afterwards can still
                    override it. */}
                <Form.Item
                  name="recipientName"
                  label={t('wz:fields.recipientName')}
                  rules={[{ required: true, message: t('wz:validation.recipientNameRequired') }]}
                >
                  <CustomerNameCombobox
                    onSelectCustomer={(customer) => form.setFieldsValue(buildRecipientFieldsFromCustomer(customer))}
                    placeholder={t('wz:placeholders.selectCustomer')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="recipientNIP"
                  label={t('wz:fields.recipientNIP')}
                >
                  <Input placeholder={t('wz:placeholders.recipientNIP')} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="recipientAddress"
                  label={t('wz:fields.recipientAddress')}
                  rules={[{ required: true, message: t('wz:validation.recipientAddressRequired') }]}
                >
                  <TextArea rows={2} placeholder={t('wz:placeholders.recipientAddress')} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="recipientPhone"
                  label={t('wz:fields.recipientPhone')}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="recipientEmail"
                  label={t('wz:fields.recipientEmail')}
                >
                  <Input type="email" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={t('wz:sections.delivery')}
            style={{ marginBottom: '24px', display: currentStep === 2 ? 'block' : 'none' }}
          >
            <Row gutter={16}>
              <Col span={24}>
                <Segmented
                  value={deliveryAddressMode}
                  onChange={handleDeliveryModeChange}
                  block
                  options={[
                    { label: t('wz:deliveryModes.registered'), value: 'registered' },
                    { label: t('wz:deliveryModes.custom'), value: 'custom' },
                  ]}
                  style={{ marginBottom: 16 }}
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  name="deliveryAddress"
                  label={t('wz:fields.deliveryAddress')}
                >
                  <TextArea
                    rows={3}
                    placeholder={t('wz:placeholders.deliveryAddress')}
                    disabled={deliveryAddressMode === 'registered'}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            style={{ marginBottom: '24px', display: currentStep === 2 ? 'block' : 'none' }}
            styles={{ body: { padding: 0 } }}
          >
            <Collapse
              ghost
              defaultActiveKey={[]}
              items={[
                {
                  key: 'transport',
                  label: (
                    <Space size={8}>
                      <span style={{ fontWeight: 600 }}>{t('wz:sections.transport')}</span>
                      <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
                        ({t('wz:transport.optionalTag')})
                      </Typography.Text>
                    </Space>
                  ),
                  children: (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="transportIds"
                  label={t('wz:fields.linkedTransports')}
                >
                  <Select
                    allowClear
                    showSearch
                    mode="multiple"
                    placeholder={t('wz:placeholders.selectTransport')}
                    options={transportOptions}
                    onChange={handleTransportSelect}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                  />
                </Form.Item>
                <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
                  {t('wz:transport.autofillHint')}
                </Typography.Paragraph>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="transportCompany"
                  label={t('wz:fields.transportCompany')}
                >
                  <Input placeholder={t('wz:placeholders.transportCompany')} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="driverName"
                  label={t('wz:fields.driverName')}
                >
                  <Input placeholder={t('wz:placeholders.driverName')} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="vehicleRegistration"
                  label={t('wz:fields.vehicleRegistration')}
                >
                  <Input placeholder={t('wz:placeholders.vehicleRegistration')} />
                </Form.Item>
              </Col>
              {canEditPurchasePrices && (
                <>
                  <Col span={8}>
                    <Form.Item
                      name="transportPrice"
                      label={t('wz:fields.transportPrice')}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        placeholder={t('wz:placeholders.transportPrice')}
                        {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="transportInvoiceNo"
                      label={t('wz:fields.transportInvoiceNo')}
                    >
                      <Input placeholder={t('wz:placeholders.transportInvoiceNo')} />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
                  ),
                },
              ]}
            />
          </Card>

          <Card title={t('wz:wizard.summary')} style={{ marginBottom: '24px', display: currentStep === 4 ? 'block' : 'none' }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('wz:fields.order')}>
                {selectedOrderSummaryLabel || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.recipientName')}>
                {form.getFieldValue('recipientName') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.recipientAddress')}>
                {form.getFieldValue('recipientAddress') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.deliveryAddress')}>
                {form.getFieldValue('deliveryAddress') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.issueDate')}>
                {form.getFieldValue('issueDate')?.format('YYYY-MM-DD') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.releaseDate')}>
                {form.getFieldValue('releaseDate')?.format('YYYY-MM-DD') || '—'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 16 }}>
              <WZItemsList compact>
                {items.filter(item => item.productName.trim() !== '').map((item, index) => (
                  <WZItemCard
                    key={`${item.productVariantId || item.productName}-${index}`}
                    index={index}
                    positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                    productLabel={t('wz:fields.productName')}
                    product={item.productName}
                    compact
                  >
                    <WZItemField label={t('wz:fields.quantity')} emphasis>{item.quantity}</WZItemField>
                    <WZItemField label={t('wz:fields.unit')}>{formatUnit(item.unit)}</WZItemField>
                  </WZItemCard>
                ))}
              </WZItemsList>
            </div>

            <Typography.Paragraph type="secondary">{t('wz:wizard.summaryHint')}</Typography.Paragraph>
            {canEditPurchasePrices && (
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="producerInvoice"
                    label={t('wz:fields.producerInvoice')}
                  >
                    <Input placeholder={t('wz:placeholders.producerInvoice')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="purchaseInvoiceAmount"
                    label={t('wz:fields.purchaseInvoiceAmount')}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      placeholder={t('wz:placeholders.purchaseInvoiceAmount')}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Card>

          {validationError && (
            <Alert
              type="error"
              message={validationError}
              style={{ marginBottom: 24 }}
            />
          )}

          <Card
            title={t('wz:sections.items')}
            className="wz-items-section"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddItem}
              >
                {t('wz:buttons.addItem')}
              </Button>
            }
            style={{ marginBottom: '24px', display: currentStep === 3 ? 'block' : 'none' }}
          >
            <WZItemsList>
              {items.map((item, index) => {
                const conversionLabel = formatConversionValue(
                  item.conversionUnit,
                  item,
                  item.quantity,
                );

                return (
                  <WZItemCard
                    key={`${item.productVariantId || 'new'}-${index}`}
                    index={index}
                    positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                    productLabel={t('wz:fields.productName')}
                    product={(
                      <ProductNameCombobox
                        value={item.productName}
                        onChange={(productName) => handleProductNameTyped(index, productName)}
                        onSelectVariant={(variantId, variant) => {
                          const newItems = [...items];
                          newItems[index] = {
                            ...newItems[index],
                            ...buildWzVariantAutofill(newItems[index], variantId, variant),
                          };
                          setItems(newItems);
                        }}
                        placeholder={t('wz:placeholders.productName')}
                      />
                    )}
                    action={(
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveItem(index)}
                        disabled={items.length === 1}
                        aria-label={t('common:delete')}
                      />
                    )}
                  >
                    <WZItemField label={t('wz:fields.dimensions')} wide>
                      {formatWZItemDimensions(item)}
                    </WZItemField>
                    <WZItemField label={t('wz:fields.quantity')}>
                      <InputNumber
                        value={item.quantity}
                        onChange={(value) => handleItemChange(index, 'quantity', value)}
                        min={WAREHOUSE_QUANTITY_MIN}
                        max={WZ_QUANTITY_MAX}
                        step={WAREHOUSE_QUANTITY_MIN}
                        precision={WAREHOUSE_QUANTITY_PRECISION}
                        {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                      />
                    </WZItemField>
                    <WZItemField label={t('wz:fields.unit')}>
                      <Select
                        value={item.unit}
                        onChange={(value) => handleItemChange(index, 'unit', value)}
                      >
                        <Option value="pcs">szt.</Option>
                        <Option value="kg">kg</Option>
                        <Option value="m">m</Option>
                        <Option value="m2">m²</Option>
                        <Option value="m3">m³</Option>
                        <Option value="l">l</Option>
                      </Select>
                    </WZItemField>
                    <WZItemField label={t('wz:fields.conversion')}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Select
                          value={item.conversionUnit ?? undefined}
                          onChange={(value) => handleItemChange(index, 'conversionUnit', value ?? null)}
                          allowClear
                          placeholder={t('wz:conversion.none')}
                        >
                          <Option value="m2">m²</Option>
                          <Option value="m3">m³</Option>
                        </Select>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {item.conversionUnit
                            ? (conversionLabel ? `= ${conversionLabel}` : t('wz:conversion.noDimensions'))
                            : ''}
                        </Typography.Text>
                      </Space>
                    </WZItemField>
                    {canEditSalePrices && (
                      <>
                        <WZItemField label={t('wz:fields.unitPrice')}>
                          <InputNumber
                            value={item.unitPrice}
                            onChange={(value) => handleItemChange(index, 'unitPrice', value)}
                            min={0}
                            precision={2}
                            {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                          />
                        </WZItemField>
                        <WZItemField label={t('wz:fields.totalValue')} emphasis>
                          <InputNumber
                            value={item.totalValue}
                            disabled
                            precision={2}
                          />
                        </WZItemField>
                      </>
                    )}
                  </WZItemCard>
                );
              })}
            </WZItemsList>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => navigate('/app/wz')}>
              {t('common:cancel')}
            </Button>
            <Space>
              <Button disabled={currentStep === 0} onClick={handleBackStep}>
                {t('common:back')}
              </Button>
              {currentStep < stepItems.length - 1 ? (
                <Button key="wz-wizard-next" type="primary" htmlType="button" onClick={handleNextStep}>
                  {t('common:next')}
                </Button>
              ) : (
                <Button key="wz-wizard-submit" type="primary" htmlType="submit" loading={submitting}>
                  {t('wz:buttons.create')}
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Space>
    </div>
  );
};

export default WZNew;
